import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getImageUrl } from '../utils/cdnUtils'
import Swal from 'sweetalert2'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import { getTenantId } from '../utils/tenantUtils'
import {
  publicDepositAccountService,
  PublicDepositAccount,
  getDecimalReservationCountdownMinutes,
  resolveDepositAccountForAmount
} from '../services/depositAccountService'
import type { PendingDepositSenderAccount } from '../services/pendingDepositDraftService'
import {
  createDecimalReservation,
  cancelDecimalReservation,
  getReservationStatus,
} from '../services/decimalDepositService'
import { slipStorageService } from '../services/slipStorageService'
import { walletTransactionService } from '../services/walletTransactionService'

const STORAGE_KEY_PREFIX = 'decimal_reservation_'

function getStorageKey(tenantId: string) {
  return `${STORAGE_KEY_PREFIX}${tenantId}`
}

function getStoredReservation(tenantId: string): {
  reservationId: string
  expiresAt: string
  amount: number
  requestedAmount?: number
  decimalSuffix?: number
} | null {
  try {
    const raw = sessionStorage.getItem(getStorageKey(tenantId))
    if (!raw) return null
    const data = JSON.parse(raw) as {
      reservationId?: string
      expiresAt?: string
      amount?: number
      requestedAmount?: number
      decimalSuffix?: number
    }
    if (data?.reservationId && data?.expiresAt) {
      return data as {
        reservationId: string
        expiresAt: string
        amount: number
        requestedAmount?: number
        decimalSuffix?: number
      }
    }
    return null
  } catch {
    return null
  }
}

function setStoredReservation(
  tenantId: string,
  data: { reservationId: string; expiresAt: string; amount: number; requestedAmount?: number; decimalSuffix?: number }
) {
  sessionStorage.setItem(getStorageKey(tenantId), JSON.stringify(data))
}

function clearStoredReservation(tenantId: string) {
  sessionStorage.removeItem(getStorageKey(tenantId))
}

const TopupReservePage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { isLoadingUserInfo, getPrimaryBankAccount } = useUserInfo()
  const stateAmount = (location.state as { amount?: string } | null)?.amount ?? ''
  const stateSenderAccount = (location.state as { senderAccount?: PendingDepositSenderAccount } | null)?.senderAccount
  const stateDepositAccount = (location.state as { depositAccount?: PublicDepositAccount } | null)?.depositAccount

  const [depositAccounts, setDepositAccounts] = useState<PublicDepositAccount[]>([])
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const [reservationId, setReservationId] = useState<string | null>(null)
  const [restoredAmount, setRestoredAmount] = useState<string>('')
  const [restoredRequestedAmount, setRestoredRequestedAmount] = useState<string>('')
  const reservationCreatedRef = useRef(false)
  const timeoutHandledRef = useRef(false)

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const tenantId = getTenantId(user)
  const userRecord = user as (typeof user & { id?: string; _id?: string }) | null
  const currentUserId = userRecord?.id ?? userRecord?._id
  const userBankAccount = getPrimaryBankAccount()

  useEffect(() => {
    if (!tenantId) return
    publicDepositAccountService.getActiveDepositAccounts(tenantId).then(setDepositAccounts)
  }, [tenantId])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const tryRestore = useCallback((): Promise<boolean> => {
    const stored = getStoredReservation(tenantId)
    if (!stored?.reservationId) return Promise.resolve(false)
    const applyStored = () => {
      const expMs = new Date(stored.expiresAt).getTime()
      const left = Math.max(0, Math.floor((expMs - Date.now()) / 1000))
      setReservationId(stored.reservationId)
      setSecondsLeft(left)
      setRestoredAmount(String(stored.amount ?? ''))
      setRestoredRequestedAmount(String(stored.requestedAmount ?? Math.floor(Number(stored.amount ?? 0))))
      reservationCreatedRef.current = true
    }
    return getReservationStatus(stored.reservationId)
      .then((res) => {
        if (res.success && res.data) {
          const { status, expiresAt, amount } = res.data
          const expMs = expiresAt ? new Date(expiresAt).getTime() : 0
          if (status === 'pending' && expMs > Date.now()) {
            setReservationId(stored.reservationId)
            setSecondsLeft(Math.max(0, Math.floor((expMs - Date.now()) / 1000)))
            setRestoredAmount(String(amount ?? stored.amount ?? ''))
            setRestoredRequestedAmount(String(res.data.requestedAmount ?? stored.requestedAmount ?? Math.floor(Number(amount ?? stored.amount ?? 0))))
            reservationCreatedRef.current = true
            return true
          }
          clearStoredReservation(tenantId)
          return false
        }
        applyStored()
        return true
      })
      .catch(() => {
        applyStored()
        return true
      })
  }, [tenantId])

  useEffect(() => {
    if (!tenantId) return

    if (!stateAmount) {
      tryRestore()
        .then((restored) => {
          if (!restored) navigate('/topup', { replace: true })
        })
        .catch(() => navigate('/topup', { replace: true }))
      return
    }

    const amountNum = parseInt(stateAmount, 10)
    if (!Number.isFinite(amountNum) || amountNum <= 0) return
    if (reservationCreatedRef.current) return
    reservationCreatedRef.current = true

    tryRestore()
      .then((restored) => {
        if (restored) return
        return Promise.all([
          getDecimalReservationCountdownMinutes(tenantId),
          publicDepositAccountService.getActiveDepositAccounts(tenantId),
        ]).then(([minutes, accounts]) => {
          const primary = stateSenderAccount || getPrimaryBankAccount()
          const webAccount = stateDepositAccount || resolveDepositAccountForAmount(accounts, amountNum)
          if (!webAccount?.accountNumber) {
            reservationCreatedRef.current = false
            Swal.fire({
              icon: 'warning',
              title: 'ไม่พบบัญชีรับโอน',
              text: 'กรุณากลับไปหน้าฝากเงินแล้วลองใหม่อีกครั้ง',
            }).then(() => navigate('/topup', { replace: true }))
            return
          }
          setSecondsLeft(minutes * 60)
          createDecimalReservation(amountNum, {
            expiresInMinutes: minutes,
            depositorName: primary?.accountName,
            depositorBank: primary?.bankName || primary?.bankCode,
            depositorAccount: primary?.accountNumber,
            webAccountName: webAccount.accountName,
            webAccountNumber: webAccount.accountNumber,
            webBankName: webAccount.bankName,
          })
            .then((res) => {
              if (res.success && res.data) {
                setReservationId(res.data.reservationId)
                const expiresAt = new Date(res.data.expiresAt).getTime()
                const left = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
                setSecondsLeft(left)
                setRestoredAmount(String(res.data.amount))
                setRestoredRequestedAmount(String(res.data.requestedAmount ?? amountNum))
                setStoredReservation(tenantId, {
                  reservationId: res.data.reservationId,
                  expiresAt: res.data.expiresAt,
                  amount: res.data.amount,
                  requestedAmount: res.data.requestedAmount ?? amountNum,
                  decimalSuffix: res.data.decimalSuffix,
                })
              }
            })
            .catch(() => {})
        })
      })
      .catch(() => {
        reservationCreatedRef.current = false
      })
  }, [getPrimaryBankAccount, navigate, stateAmount, stateDepositAccount, stateSenderAccount, tenantId, tryRestore])

  useEffect(() => {
    if (secondsLeft == null || secondsLeft <= 0) return
    const t = setInterval(() => setSecondsLeft((s) => (s == null || s <= 0 ? 0 : s - 1)), 1000)
    return () => clearInterval(t)
  }, [secondsLeft])

  useEffect(() => {
    if (secondsLeft !== 0 || timeoutHandledRef.current) return
    if (!reservationId) return
    timeoutHandledRef.current = true
    clearStoredReservation(tenantId)
    Swal.fire({
      icon: 'info',
      title: 'หมดเวลาการจอง',
      text: 'กรุณาเริ่มต้นใหม่ที่หน้าฝากเงิน',
    }).then(() => navigate('/topup', { replace: true }))
  }, [secondsLeft, reservationId, tenantId, navigate])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('คัดลอกเรียบร้อยแล้ว!')
    } catch {
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      toast.success('คัดลอกเรียบร้อยแล้ว!')
    }
  }

  const handleCancelReservation = async () => {
    if (!reservationId) return
    const { value } = await Swal.fire({
      icon: 'question',
      title: 'ยกเลิกการจอง?',
      text: 'ต้องการยกเลิกการจองโอนเงินนี้หรือไม่',
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกการจอง',
      cancelButtonText: 'ไม่',
    })
    if (!value) return
    await cancelDecimalReservation(reservationId)
    clearStoredReservation(tenantId)
    toast.success('ยกเลิกการจองแล้ว')
    navigate('/topup', { replace: true })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      toast.error('ขนาดไฟล์สลิปไม่ควรเกิน 2MB')
      e.target.value = ''
      return
    }
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
    const hasAllowedMimeType = allowedTypes.includes(String(file.type || '').toLowerCase())
    const hasAllowedExtension = allowedExtensions.includes(fileExtension)
    if (!hasAllowedMimeType && !hasAllowedExtension) {
      toast.error('กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น (JPG, PNG, GIF, WebP)')
      e.target.value = ''
      return
    }
    setSelectedFile(file)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(URL.createObjectURL(file))
    toast.success('เลือกไฟล์สลิปแล้ว')
  }

  const removeFile = () => {
    setSelectedFile(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
    const fileInput = document.getElementById('formFileReserve') as HTMLInputElement
    if (fileInput) fileInput.value = ''
    toast.success('ลบไฟล์เรียบร้อยแล้ว')
  }

  const handleConfirm = async () => {
    if (!selectedFile) {
      toast.error('กรุณาแนบสลิป')
      return
    }
    if (!currentUserId || !user?.username) {
      toast.error('ไม่พบข้อมูลผู้ใช้')
      return
    }

    try {
      const transferTime = new Date()
      const amountNum = parseFloat(displayAmount)

      const uploadResult = await slipStorageService.uploadSlip(tenantId, {
        userId: currentUserId,
        username: user.username,
        transferAmount: amountNum,
        transferTime,
        transferDate: transferTime,
        slipFile: selectedFile,
      })

      if (uploadResult.success) {
        const uploadData = Array.isArray(uploadResult.data) ? uploadResult.data[0] : uploadResult.data
        const resolvedDepositAccount = resolveDepositAccountForAmount(
          depositAccounts,
          parseFloat(displayRequestedAmount || displayAmount),
          { channel: 'bank' }
        )
        const depositResult = await walletTransactionService.createDeposit({
          amount: amountNum,
          paymentMethod: 'bank_transfer',
          reference: reservationId || undefined,
          slipData: {
            imageUrl: String(uploadData?.storageUrl || ''),
            bankCode: resolvedDepositAccount?.bankCode,
            accountNumber: resolvedDepositAccount?.accountNumber,
          },
          metadata: {
            note: 'user-classic submitted decimal reservation deposit with slip attachment',
            depositMethod: 'decimal_reservation',
            selectedDepositAccountId: resolvedDepositAccount?._id,
            selectedDepositAccountNumber: resolvedDepositAccount?.accountNumber,
            selectedDepositAccountName: resolvedDepositAccount?.accountName,
            selectedDepositBankCode: resolvedDepositAccount?.bankCode,
            selectedDepositBankName: resolvedDepositAccount?.bankName,
            senderAccount: stateSenderAccount || (userBankAccount ? {
              bankCode: userBankAccount.bankCode,
              bankName: userBankAccount.bankName,
              accountNumber: userBankAccount.accountNumber,
              accountName: userBankAccount.accountName,
            } : undefined),
            receiverAccount: resolvedDepositAccount ? {
              accountId: resolvedDepositAccount._id,
              bankCode: resolvedDepositAccount.bankCode,
              bankName: resolvedDepositAccount.bankName,
              accountNumber: resolvedDepositAccount.accountNumber,
              accountName: resolvedDepositAccount.accountName,
            } : undefined,
            sourceChannel: 'slip',
            submittedSlipImageUrl: String(uploadData?.storageUrl || '') || undefined,
            reservationId: reservationId || undefined,
          },
        })

        if (!depositResult.success) {
          throw new Error(depositResult.message || 'ส่งรายการฝากไม่สำเร็จ')
        }

        clearStoredReservation(tenantId)
        toast.success(depositResult.message || 'ยืนยันการโอนเงินเรียบร้อยแล้ว ระบบส่งรายการเข้าคิวตรวจสอบแล้ว')
        navigate('/dashboard')
      } else {
        toast.error(uploadResult.message || 'เกิดข้อผิดพลาดในการบันทึกสลิป')
      }
    } catch (error: any) {
      console.error('Confirm transfer failed:', error)
      toast.error(error?.message || 'เกิดข้อผิดพลาดในการยืนยันการโอนเงิน')
    }
  }

  const displayAmount = restoredAmount || stateAmount || '0'
  const displayRequestedAmount = stateAmount || restoredRequestedAmount || displayAmount
  const depositAccount = resolveDepositAccountForAmount(depositAccounts, parseFloat(displayRequestedAmount))
  const timeLeft = secondsLeft ?? 0

  if (!stateAmount && !restoredAmount) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="text-center c-dark py-5">กำลังโหลด...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/topup')
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าหลัก
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-wallet me-2"></i>
                      แจ้งเติมเครดิต (ฝากทศนิยม)
                    </div>

                    <div className="bdr-5 bgc-4 c-red p-2 mb-2">
                      <div className="row mx--1">
                        <div className="col-12 px-1 mb-2">
                          <div className="c-dark t-medium">
                            ข้อมูลบัญชีธนาคารที่ต้องโอน
                          </div>
                        </div>

                        <div className="col-12 col-lg-6 px-1 mb-2">
                          <div className="wrap align-items-start bgc-7 bdr-5 p-2">
                            {isLoadingUserInfo ? (
                              <div className="txt-b w-100 py-2">
                                <h6 className="c-white bgg-4 bdr-5 px-2 mb-1" style={{ width: 'fit-content' }}>
                                  โอนจากบัญชี
                                </h6>
                                <p className="c-muted mb-0">กำลังโหลดข้อมูลบัญชี...</p>
                              </div>
                            ) : userBankAccount ? (
                              <>
                                <div className="img-b bdr-5 bgc-4">
                                  {userBankAccount.imageUrl ? (
                                    <img
                                      src={userBankAccount.imageUrl}
                                      alt={userBankAccount.bankName}
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement
                                        target.src = getImageUrl('icons-default-bank.png')
                                      }}
                                    />
                                  ) : (
                                    <img src={getImageUrl('icons-default-bank.png')} alt="User Bank" />
                                  )}
                                </div>
                                <div className="txt-b ms-3">
                                  <h6 className="c-white bgg-4 bdr-5 px-2 mb-1" style={{ width: 'fit-content' }}>
                                    โอนจากบัญชี
                                  </h6>
                                  <h6 className="c-dark">{userBankAccount.bankName}</h6>
                                  <h5 className="c-red t-medium mb-0">{userBankAccount.accountNumber}</h5>
                                  <h6 className="c-white bgg-5 bdr-5 px-2 mb-1" style={{ width: 'fit-content' }}>
                                    ชื่อบัญชี
                                  </h6>
                                  <h5 className="c-dark t-medium fs-16">{userBankAccount.accountName}</h5>
                                </div>
                              </>
                            ) : (
                              <div className="txt-b w-100 py-2">
                                <h6 className="c-white bgg-4 bdr-5 px-2 mb-1" style={{ width: 'fit-content' }}>
                                  โอนจากบัญชี
                                </h6>
                                <p className="c-dark mb-1">ยังไม่ได้ผูกบัญชีธนาคาร</p>
                                <p className="c-muted fs-14 mb-2">กรุณาไปเพิ่มบัญชีที่หน้าโปรไฟล์ก่อนใช้งานฝากทศนิยม</p>
                                <a
                                  href="#"
                                  className="txt-link-d"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    navigate('/profile')
                                  }}
                                >
                                  <i className="fas fa-user-cog me-1"></i>ไปหน้าโปรไฟล์
                                </a>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-12 col-lg-6 px-1 mb-2">
                          <div className="wrap align-items-start bgc-7 bdr-5 p-2 position-relative">
                            <div className="img-b bdr-5 bgc-4">
                              {depositAccount?.bankIcon ? (
                                <img
                                  src={depositAccount.bankIcon}
                                  alt={depositAccount.bankName}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    target.src = getImageUrl('icons-default-bank.png')
                                  }}
                                />
                              ) : (
                                <img src={getImageUrl('icons-default-bank.png')} alt="Default Bank" />
                              )}
                            </div>
                            <button
                              type="button"
                              className="button-copy wrap bdr-5 border-0 c-dark py-1"
                              onClick={() => copyToClipboard(depositAccount?.accountNumber || '')}
                            >
                              <i className="fas fa-link me-1"></i>
                              <small> คลิกคัดลอก </small>
                            </button>
                            <div className="txt-b ms-3">
                              <h6 className="c-white bgg-4 bdr-5 px-2 mb-1" style={{ width: 'fit-content' }}>
                                โอนถึงบัญชี
                              </h6>
                              <h6 className="c-dark">{depositAccount?.bankName || 'ธนาคาร'}</h6>
                              <h5 className="c-red t-medium">{depositAccount?.accountNumber || '**********'}</h5>
                              <h6 className="c-white bgg-5 bdr-5 px-2 mb-1" style={{ width: 'fit-content' }}>
                                ชื่อบัญชี
                              </h6>
                              <h5 className="c-dark t-medium fs-16">
                                {depositAccount?.accountName || 'เจ้าของเว็บ'}
                              </h5>
                              {depositAccount?.description && (
                                <p className="c-dark fs-12 mt-1 mb-0">{depositAccount.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row mx--1">
                      <div className="col-12 px-1 mb-2">
                        <div className="text-center pt-5 p-2 bdr-5 bdc-1 bgg-1 position-relative">
                          <button
                            type="button"
                            className="button-copy wrap bdr-5 border-0 c-dark py-1"
                            onClick={() => copyToClipboard(displayAmount)}
                          >
                            <i className="fas fa-link me-1"></i>
                            <small> คลิกคัดลอก </small>
                          </button>

                          <h5 className="c-yellow">กรุณาโอนเงินจำนวนยอดนี้เท่านั้น</h5>
                          <h1 className="t-medium c-white">{displayAmount}</h1>
                          <p className="mt-2 mb-0 c-white fs-14">
                            ยอดตั้งต้น {displayRequestedAmount} บาท ระบบสุ่มเลขทศนิยมให้อัตโนมัติ
                          </p>

                          <h6 className="t-regular c-white">โอนก่อนหมดเวลา</h6>

                          <div className="bgc-4 bdr-5 mt-2">
                            <h5 className="c-red">{formatTime(timeLeft)}</h5>
                          </div>

                          <h6 className="t-regular c-white mt-2">เหลือ(นาที)</h6>
                        </div>
                      </div>

                      <div className="col-12 px-1 mb-2">
                        <div className="bgc-7 bdr-5 px-2 py-2 mb-2">
                          <p className="c-dark mb-2">
                            แนบไฟล์ (สลิป)
                            <span className="c-red fs-14"> *** ขนาดไฟล์สลิปไม่ควรเกิน 2MB ***</span>
                          </p>
                          <div className="alert alert-warning fs-12 mb-2">
                            <i className="fas fa-shield-alt me-1"></i>
                            <strong>ข้อควรระวัง:</strong> กรุณาอัปโหลดเฉพาะสลิปการโอนเงินจริงเท่านั้น
                            ระบบจะตรวจสอบความถูกต้องของไฟล์อัตโนมัติ
                          </div>
                          <div className="bgc-4 p-2 bdr-5">
                            <input
                              className="form-control"
                              type="file"
                              id="formFileReserve"
                              accept="image/*"
                              onChange={handleFileChange}
                            />
                            {!selectedFile && (
                              <div className="text-center mt-2">
                                <small className="c-muted">
                                  <i className="fas fa-upload me-1"></i>
                                  ยังไม่ได้เลือกไฟล์
                                </small>
                              </div>
                            )}
                          </div>

                          {previewUrl && selectedFile && (
                            <div className="mt-3">
                              <div className="bgc-7 bdr-5 p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h6 className="c-dark mb-0">
                                    <i className="fas fa-image me-2"></i>
                                    ตัวอย่างสลิปที่อัปโหลด
                                  </h6>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={removeFile}
                                    title="ลบไฟล์"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                                <div className="text-center">
                                  <img
                                    src={previewUrl}
                                    alt="Preview"
                                    className="img-fluid bdr-5 shadow-sm"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '300px',
                                      objectFit: 'contain',
                                      border: '2px solid #ddd',
                                    }}
                                  />
                                </div>
                                <div className="mt-2 text-center">
                                  <small className="c-dark">
                                    <i className="fas fa-file-image me-1"></i>
                                    {selectedFile.name}
                                  </small>
                                  <br />
                                  <small className="c-muted">
                                    ขนาด: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                  </small>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <h6 className="c-dark">
                          กรุณาตรวจสอบข้อมูลของท่านให้ถูกต้อง และกดปุ่ม
                          <span className="c-red">ยืนยันการแจ้งโอนเงิน</span>
                        </h6>
                      </div>

                      <div className="col-12 px-1">
                        <button
                          type="button"
                          className="check-button w-100 bgg-1 bdc-1 c-white"
                          onClick={handleConfirm}
                        >
                          ยืนยันการโอนเงิน
                        </button>
                      </div>

                      <div className="col-12 px-1 mt-2 text-center">
                        <button
                          type="button"
                          className="btn btn-outline-danger bdr-5"
                          onClick={handleCancelReservation}
                        >
                          ยกเลิกการจองโอนเงิน
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TopupReservePage
